import { REPORT_TYPES } from '../../../consts/reportTypes';
import { TRANSFORMATION_TYPES } from '../../../consts/transformationTypes';
import { isFromScratch, isNilReturnPeriod } from '../../../pages/Project/Quarters/data';
import { designPath } from '../../../utils/design';
import { buildStageLabels } from '../../../hooks/useWorkflowStages';

const DONE = 'Completed';
const MANUAL = TRANSFORMATION_TYPES.MANUAL;
const NOT_STARTED = 'Not started';

/* Reading the period's own fields, with the two traps they carry. */
const stats = (period, key) => period?.[key] || {};

// Shared so the default never changes identity and re-memoises everything downstream.
const EMPTY_ABANDONED = [];

/** How many of a stage's sheets are being worked on this instant. */
export const inFlightCount = (period, step) =>
  step === 'transformation'
    ? stats(period, 'transformation_stats').in_progress_transformation_number || 0
    : stats(period, 'extraction_stats').in_progress_extractions_number || 0;

const extractionRunning = period =>
  (stats(period, 'extraction_stats').in_progress_extractions_number || 0) > 0;

const extractionTouched = period => {
  const s = stats(period, 'extraction_stats');
  return (
    (s.successful_extractions_number || 0) +
      (s.failed_extractions_number || 0) +
      (s.warned_extractions_number || 0) +
      (s.in_progress_extractions_number || 0) >
    0
  );
};

const extractionTrouble = period => {
  const s = stats(period, 'extraction_stats');
  return (
    (s.failed_extractions_number || 0) +
    (s.warned_extractions_number || 0) +
    (s.not_completed_extractions_number || 0)
  );
};

const transformationRunning = period =>
  (stats(period, 'transformation_stats').in_progress_transformation_number || 0) > 0;

const transformationTrouble = period => {
  const s = stats(period, 'transformation_stats');
  return (s.failed_transformations_number || 0) + (s.not_completed_transformations_number || 0);
};

const REPORT_SETTLED = [DONE, 'Failed'];

const reportRunning = period =>
  !!period?.report_task && !REPORT_SETTLED.includes(period.report_task.status);

/** The same reading for the transformation, and for the same reason. */
const transformationTouched = period => {
  const s = stats(period, 'transformation_stats');
  return (
    (s.successful_transformations_number || 0) +
      (s.failed_transformations_number || 0) +
      (s.warned_transformations_number || 0) +
      (s.in_progress_transformation_number || 0) >
    0
  );
};

/* Past the sheet stages, for periods too old to carry per-run counts. */
const pipelinePast = period => period.report_task?.status === DONE;

/** automate-transformation answered with no per-table task: there was nothing to transform. */
export const startedNoTransformation = response =>
  Array.isArray(response?.automation_transformation_tasks) &&
  response.automation_transformation_tasks.length === 0;

/** No sheet has actions to carry over or of its own, so the transformation has nothing to run. */
export const nothingToTransform = rows => {
  const list = Array.isArray(rows) ? rows : Array.isArray(rows?.data) ? rows.data : null;
  return (
    !!list?.length &&
    list.every(row => !row?.previous_transformation_id && !row?.current_transformation_id)
  );
};

export const runStatus = (
  period,
  reportType,
  {
    mapped = true,
    moving = null,
    mappingTried = false,
    jurisdictionsTried = false,
    abandoned = EMPTY_ABANDONED,
    skipTransformation = false
  } = {}
) => {
  /* The window only bridges the gap BETWEEN sheets. */
  const stillMoving = step => {
    if (!moving || !moving(step)) return false;
    const s = stats(
      period,
      step === 'transformation' ? 'transformation_stats' : 'extraction_stats'
    );
    const outstanding =
      step === 'transformation'
        ? s.not_completed_transformations_number || 0
        : s.not_completed_extractions_number || 0;
    return outstanding > 0;
  };
  if (!period) return { status: 'done' };

  /* Nothing here reads `*_progress_status` for whether a stage has run. */
  const isConversion = reportType === REPORT_TYPES.CONVERSION;
  const isCustom = reportType === REPORT_TYPES.CUSTOM;
  /* Nil Return is an Annex IV filing concept; other workflows keep their required data stages. */
  const isNilReturn = reportType === REPORT_TYPES.ANNEX_IV && isNilReturnPeriod(period);
  /* A period built from scratch is not an automation, and the server means that literally. */
  if (isFromScratch(period)) return { status: 'blocked', reason: 'scratch' };

  if (!isCustom) {
    if (!period.fund_count) return { status: 'step', step: 'funds' };

    if (!period.jurisdiction_count && !jurisdictionsTried)
      return { status: 'step', step: 'jurisdictions' };
  }

  /* The one thing a machine cannot do. */
  if (!isNilReturn && !period.file_count) return { status: 'blocked', reason: 'files' };
  /* Carrying last period's mapping is the machine's work; a conversion has no mapping at all. */
  if (!isConversion && !isNilReturn && !mapped) {
    return mappingTried
      ? { status: 'blocked', reason: 'mapping' }
      : { status: 'step', step: 'mapping' };
  }

  if (!isConversion && !isNilReturn) {
    if (!abandoned.includes('extraction')) {
      if (extractionRunning(period) || stillMoving('extraction'))
        return { status: 'busy', step: 'extraction' };
    }
    if (!extractionTouched(period) && !pipelinePast(period))
      return { status: 'step', step: 'extraction' };

    if (!abandoned.includes('transformation')) {
      if (transformationRunning(period) || stillMoving('transformation'))
        return { status: 'busy', step: 'transformation' };
    }
    if (!skipTransformation && !transformationTouched(period) && !pipelinePast(period))
      return { status: 'step', step: 'transformation' };
  }

  if (isCustom) return { status: 'done' };

  if (reportRunning(period)) return { status: 'busy', step: 'report' };

  const noReportHere =
    !period.report_task &&
    (period.report_progress_status === NOT_STARTED || transformationTouched(period));
  if (noReportHere) return { status: 'step', step: 'report' };

  return { status: 'done' };
};

/** Whether a period has anything left worth pressing a run button for. */
export const canRunPeriod = (period, reportType, options) => {
  const state = runStatus(period, reportType, options);
  if (state.status === 'step' || state.status === 'busy') return true;
  /* Armed rather than refused. */
  return state.status === 'blocked' && (state.reason === 'files' || state.reason === 'mapping');
};

/* Whether the run should stop and ask before taking this step. */
export const askBefore = (
  period,
  step,
  { canRetry = true, missingAifs = [], skipTransformation = false } = {}
) => {
  /* A fund with no column is simply absent from the report — worth stopping while it is ahead. */
  if (step === 'report' && missingAifs.length)
    return {
      step,
      reviewStage: 'source_files',
      title: `${missingAifs.length} ${
        missingAifs.length === 1 ? 'AIF' : 'AIFs'
      } would be left out of the report`,
      body: `The report would be built without ${
        missingAifs.length === 1 ? 'it' : 'them'
      }. Continue?`
    };

  /* Asked before the transformation, or before the report when there is no transformation to run. */
  const afterExtraction = skipTransformation ? 'report' : 'transformation';
  if (step === afterExtraction && extractionTrouble(period) > 0)
    return {
      step,
      reviewStage: 'extraction',
      retry: canRetry,
      title: `${extractionTrouble(period)} of ${
        extractionTrouble(period) +
        (stats(period, 'extraction_stats').successful_extractions_number || 0)
      } sheets did not extract cleanly`,
      body: canRetry
        ? 'They can be extracted again — anything that has since been fixed, a mapping or a file, is picked up. Or the transformation and the report can be built on what came through.'
        : 'Transformation and the report would be built on what came through. Continue?'
    };

  if (step === 'report' && !skipTransformation && transformationTrouble(period) > 0)
    return {
      step,
      title: `${transformationTrouble(period)} transformation${
        transformationTrouble(period) === 1 ? '' : 's'
      } did not finish`,
      body: 'The report would be built without them. Continue?'
    };

  return null;
};

const progressOf = (period, key, doneFields, pendingFields) => {
  const s = stats(period, key);
  const done = doneFields.reduce((sum, field) => sum + (s[field] || 0), 0);
  const pending = pendingFields.reduce((sum, field) => sum + (s[field] || 0), 0);
  const total = done + pending;
  return total > 0 ? { done, total } : null;
};

const extractionProgress = period =>
  progressOf(
    period,
    'extraction_stats',
    ['successful_extractions_number', 'failed_extractions_number', 'warned_extractions_number'],
    [
      'in_progress_extractions_number',
      'queued_extractions_number',
      'not_completed_extractions_number'
    ]
  );

const transformationProgress = period =>
  progressOf(
    period,
    'transformation_stats',
    [
      'successful_transformations_number',
      'failed_transformations_number',
      'warned_transformations_number'
    ],
    ['in_progress_transformation_number', 'not_completed_transformations_number']
  );

/* The order the run takes its steps in, and which stage on the stepper each one is. */
export const RUN_ORDER = [
  'funds',
  'jurisdictions',
  'mapping',
  'extraction',
  'transformation',
  'report'
];

const orderFor = reportType => {
  if (reportType === REPORT_TYPES.CONVERSION) return ['funds', 'jurisdictions', 'report'];
  if (reportType === REPORT_TYPES.CUSTOM) return ['mapping', 'extraction', 'transformation'];
  return RUN_ORDER;
};

/* Read clean — template there, every AIF with a column, file not still being read — and there is
   nothing left for a press to decide. */
/* A run is over when its period can't be read and nothing was, or the server says it is gone. */
export const runLostItsPeriod = ({ isError, data, error }) =>
  !!isError && (!data || error?.response?.status === 404);

export const workbookLetsItGo = ({
  isRead = false,
  error = null,
  withoutTemplate = [],
  missing = [],
  reading = false
} = {}) => !!isRead && !error && !withoutTemplate.length && !missing.length && !reading;

export const STAGE_FOR_STEP = {
  funds: 'aifs',
  jurisdictions: 'jurisdictions',
  mapping: 'setup',
  extraction: 'extraction',
  transformation: 'transformation',
  report: 'report'
};

export const stepsAhead = (period, reportType, known = null) => {
  const state = known || runStatus(period, reportType);
  if (state.status === 'done') return [];
  if (state.status === 'blocked' && state.reason === 'scratch') return [];

  const order = orderFor(reportType);

  /* Waiting on the files, which is where a brand-new period stands. */
  if (state.status === 'blocked')
    return order.filter(step => step !== 'funds' && step !== 'jurisdictions');

  const at = order.indexOf(state.step);
  return at < 0 ? [] : order.slice(at + 1);
};

/* The sheets a re-extraction should take, out of the period's detailed extraction rows. */
export const sheetsToReExtract = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => row?.automation_extraction_type !== MANUAL)
    .filter(
      row =>
        !['Completed', 'In progress', 'Queued'].includes(row?.latest_automation_task?.status ?? '')
    )
    .map(row => row?.sheet_mapping_id)
    .filter(Boolean);

/* The sheets a stage did not get through, by name, with what the server said about each. */
export const troubleSheets = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => row?.automation_extraction_type !== MANUAL)
    .filter(
      row =>
        !['Completed', 'In progress', 'Queued'].includes(row?.latest_automation_task?.status ?? '')
    )
    .map(row => {
      const task = row?.latest_automation_task;
      return {
        key: row?.sheet_mapping_id ?? row?.sheet_id,
        sheet: row?.sheet_name || 'A sheet',
        file: row?.file_name || null,
        /* "Not extracted" is what the issues drawer calls a sheet with no task at all. */
        state: task?.status || 'Not extracted',
        why: task?.error_message || task?.system_error_message || null
      };
    });

/* One tile per sheet, for the stage the run is working through. */
const REPORT_PHASES = {
  'Loading to Snowflake': 'loading the data',
  'Report generate': 'building the report'
};

/* What the backend calls the phase a sheet is in, said in words a reader has a use for. */
const PHASE_WORDS = {
  HEADER_AND_FOOTER_INDEXES: 'finding the header',
  'Fund mapping': 'matching funds',
  Extraction: 'extracting'
};

export const runTiles = (step, rows) => {
  const list = Array.isArray(rows) ? rows : (rows?.results ?? rows?.data ?? []);
  if (!Array.isArray(list) || !list.length) return [];

  const taskOf = row => (step === 'transformation' ? row?.task : row?.latest_automation_task);

  const stateOf = row => {
    /* `Queued` counts as running: the server has the sheet, it has just not started on it. */
    const status =
      step === 'transformation'
        ? row?.task?.status === 'In progress'
          ? 'In progress'
          : row?.status
        : row?.latest_automation_task?.status;

    if (status === DONE) return 'done';
    if (status === 'Warned') return 'warned';
    if (status === 'Failed' || status === 'Cancelled') return 'failed';
    if (status === 'In progress' || status === 'Queued') return 'running';
    return 'waiting';
  };

  return list.map((row, index) => {
    const task = taskOf(row);
    const queued = task?.status === 'Queued';
    return {
      key: row?.sheet_mapping_id ?? row?.id ?? String(index),
      label: row?.sheet_name ?? row?.category ?? row?.table_name ?? `Sheet ${index + 1}`,
      /* Which workbook it came out of, and how sure the extraction was. */
      file: row?.file_name ?? null,
      category: row?.category ?? null,
      confidence: typeof row?.confidence_score === 'number' ? row.confidence_score : null,
      state: stateOf(row),
      /* Queued says more than the step it has not reached yet. */
      phase: queued ? 'waiting its turn' : PHASE_WORDS[task?.step] || null,
      error: task?.error_message || task?.system_error_message || null
    };
  });
};

/** The one line that names what a grid of tiles is doing right now. */
export const tilesCaption = tiles => {
  if (!tiles.length) return null;
  const settled = tiles.filter(tile => tile.state !== 'waiting' && tile.state !== 'running').length;
  const running = tiles.find(tile => tile.state === 'running');
  const failed = tiles.filter(tile => tile.state === 'failed').length;

  const parts = [`${settled} of ${tiles.length}`];
  /* The sheet's name as the workbook spells it, and what is being done to it. */
  if (running)
    parts.push(running.phase ? `${running.label} — ${running.phase}` : `${running.label} now`);
  if (failed) parts.push(`${failed} did not come through`);
  return parts.join(' · ');
};

export const STEP_LABELS = {
  funds: 'Funds',
  jurisdictions: 'Jurisdictions',
  mapping: 'Mapping',
  extraction: 'Extraction',
  transformation: 'Transformation',
  report: 'Report'
};

export const stepLabels = reportType => {
  const stages = buildStageLabels(reportType);
  const named = {};
  for (const [step, stage] of Object.entries(STAGE_FOR_STEP))
    named[step] = stages[stage] || STEP_LABELS[step];
  return named;
};

/* One line for the band: what is happening, and how far in. */
export const runNarration = (state, period, reportType) => {
  const isCustom = reportType === REPORT_TYPES.CUSTOM;
  const isConversionRun = reportType === REPORT_TYPES.CONVERSION;
  const reportNoun = isCustom ? 'output table' : 'report';
  const labels = stepLabels(reportType);

  const line = (text, progress = null) => ({
    text,
    progress,
    detail: progress
      ? `${progress.done} of ${progress.total} · ${Math.round((progress.done / progress.total) * 100)}%`
      : null
  });

  /* A step that has just finished, held on screen for a moment before the next one starts. */
  if (state.status === 'settled') {
    const counted =
      state.step === 'extraction'
        ? extractionProgress(period)
        : state.step === 'transformation'
          ? transformationProgress(period)
          : null;
    return line(`${labels[state.step] || 'That step'} finished`, counted);
  }

  if (state.status === 'asking')
    return {
      text: 'Waiting for your answer',
      progress: null,
      detail: 'the run goes on when the question above is answered'
    };

  /* Asked for, and not yet visible in the period. */
  if (state.status === 'asked')
    return {
      /* The same words the step's own line uses once it is running. */
      text:
        state.step === 'report'
          ? `Starting the ${reportNoun}`
          : `Starting ${(labels[state.step] || state.step).toLowerCase()}`,
      progress: null,
      detail: 'waiting for the server to pick it up'
    };

  if (state.status === 'ready')
    return {
      ...line('The files are in'),
      detail: 'press start and the rest of this period runs by itself'
    };

  if (state.status === 'blocked' && state.reason === 'files')
    return {
      ...line('Waiting for this period’s files'),
      detail: 'add them and the rest runs by itself'
    };

  if (state.status === 'blocked' && state.reason === 'reading')
    return {
      ...line(isConversionRun ? 'Reading the workbook' : 'Reading the files just added'),
      /* A conversion pairs nothing: it waits for the file to be read, then converts it. */
      detail: isConversionRun
        ? `the ${reportNoun} is built as soon as it is read`
        : 'the run goes on once the pairing is written'
    };

  if (state.status === 'blocked' && state.reason === 'mapping')
    return {
      ...line('Nothing came across into the mapping'),
      detail: 'the sheets need naming before the rest can run'
    };

  if (state.status === 'blocked') return line('This period is set up by hand');
  if (state.status === 'done')
    /* A custom filing has no report step for the machine to take. */
    return line(
      isCustom
        ? 'Everything the machine can do is done — the output table is yours'
        : 'Everything that could run has run'
    );

  const step = state.step;
  const busy = state.status === 'busy';

  if (step === 'extraction')
    return busy ? line('Extracting', extractionProgress(period)) : line('Starting extraction');

  if (step === 'transformation')
    return busy
      ? line('Transforming', transformationProgress(period))
      : line('Starting transformation');

  if (step === 'report') {
    if (!busy) return line(`Starting the ${reportNoun}`);
    const phase = REPORT_PHASES[period?.report_task?.step] || null;
    return { ...line(`Generating the ${reportNoun}`), detail: phase };
  }

  return line(`Copying ${labels[step]?.toLowerCase() || step}`);
};

export const placeFor = (state, { workflowId, periodId, reportType }) => {
  if (!workflowId || !periodId) return null;
  const period_ = `${workflowId}/${periodId}`;

  if (state.status === 'blocked')
    return (
      { files: `/project/quaters/${period_}/upload-files`, mapping: `/setup/${period_}` }[
        state.reason
      ] ?? null
    );

  if (state.status === 'asking') return null;

  const step = state.step;

  /* Nowhere, for the three that are over before you have read the page. */
  if (step === 'funds' || step === 'jurisdictions' || step === 'mapping') return null;
  if (step === 'extraction') return `/data-extraction/${period_}?tab=extraction`;
  if (step === 'transformation') return `/data-extraction/${period_}?tab=transformation`;
  if (step === 'report')
    return designPath(
      reportType === REPORT_TYPES.CUSTOM ? `/output/${period_}` : `/report-v2/${period_}`,
      `/output-tables/${period_}`
    );

  return null;
};

/* Two addresses are the same place when they are the same page showing the same stage. */
/* Whether an address is a page where the work is a person's. */
export const isHandPlace = address => {
  if (typeof address !== 'string') return false;
  const path = address.split('?')[0];
  return path.endsWith('/upload-files') || path.startsWith('/setup/');
};

export const samePlace = (a, b) => {
  if (!a || !b) return false;
  const at = (value = '') => {
    const [path, search = ''] = value.split('?');
    return `${path}|${new URLSearchParams(search).get('tab') || ''}`;
  };
  return at(a) === at(b);
};
